import { OrganisationPermissionConstants, OrganisationTypeConstants } from "../../../../../../constants";
import { SelectData } from "../../../../../../models";
import { useAuth } from "../../../../../../useAuth";
import {
  ActionButtons,
  Column,
  ErrorBox,
  InformationBox,
  RadioButtonGroup,
  ReadonlyText,
  Row,
  Select,
} from "../../../../../../widget";
import { useEditForm } from "./useEditForm";

interface EditFormProps {
  userUuid: string | undefined;
  defaultRoleValue: string;
  defaultActiveValue: boolean;
  roles: SelectData | undefined;
  rolesInfo: string[];
  rowVersion: number;
  userSettingsRoute: string;
  firstName: string;
  lastName: string;
  email: string;
}

export const EditForm = ({
  userUuid,
  defaultRoleValue,
  defaultActiveValue,
  roles,
  rolesInfo,
  rowVersion,
  userSettingsRoute,
  firstName,
  lastName,
  email,
}: EditFormProps): JSX.Element => {
  const { hasPermission } = useAuth();
  const {
    radioValues,
    role,
    active,
    isHandleSubmitLoading,
    currentUserType,
    setRole,
    setActive,
    handleSubmit,
    handleCancel,
    errors,
  } = useEditForm(userUuid, defaultRoleValue, defaultActiveValue, rowVersion, userSettingsRoute);
  const canManageOrganisationUsers = hasPermission(OrganisationPermissionConstants.WRITE_ORGANISATION_USERS);

  return (
    <>
      {errors && errors.length > 0 && (
        <Row spacingV="ml">
          <Column span={5}>
            <ErrorBox title="Please solve the below issues before saving" text={errors.map((error) => error.message)} />
          </Column>
        </Row>
      )}

      <form onSubmit={(e) => handleSubmit(e)}>
        <Row spacingV="ll">
          <Column span="4_half">
            <ReadonlyText label="First name" value={firstName} />
          </Column>
          <Column offset={1} span="4_half">
            <ReadonlyText label="Email address" value={email} />
          </Column>
        </Row>
        <Row spacingV="ll">
          <Column span="4_half">
            <ReadonlyText label="Last name" value={lastName} />
          </Column>
          {roles && (
            <Column offset={1} span="4_half">
              <Select
                label="Role"
                initialKey={role}
                data={roles}
                onChange={(value) => setRole(value)}
                corners={currentUserType === OrganisationTypeConstants.ASSET_MANAGER ? "rounded" : "default"}
                disabled={!canManageOrganisationUsers}
              />
            </Column>
          )}
        </Row>
        <Row spacingV="xl">
          <Column span={12}>
            <InformationBox
              variant="info"
              title="What can each role do?"
              corners={currentUserType === OrganisationTypeConstants.ASSET_MANAGER ? "rounded" : "default"}
            >
              <ul>
                {rolesInfo.map((info, index) => (
                  <div key={info}>
                    <li>{info}</li>
                    {index !== rolesInfo.length - 1 && <br />}
                  </div>
                ))}
              </ul>
            </InformationBox>
          </Column>
        </Row>
        <Row spacingV="xl">
          <Column span={4}>
            <RadioButtonGroup
              data={radioValues}
              value={active}
              disabled={!canManageOrganisationUsers}
              onChange={(e) => {
                setActive(e.target.value);
              }}
            />
          </Column>
        </Row>
        {canManageOrganisationUsers && (
          <Row>
            <Column span={2}>
              <ActionButtons onCancel={handleCancel} isLoading={isHandleSubmitLoading} />
            </Column>
          </Row>
        )}
      </form>
    </>
  );
};
